<script>
  /**
   * A tenant's portal members and pending invitations.
   *
   * Takes the tenant as a prop and uses no router hooks, so it stays usable
   * anywhere (see App.svelte's note about components outside RouterView).
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../../stores/authStore.svelte.js';
  import ConfirmModal from '../ConfirmModal.svelte';

  let { tenant } = $props();

  let loading = $state(true);
  let error = $state(null);
  let members = $state([]);
  let invitations = $state([]);
  let properties = $state([]);

  let showInvite = $state(false);
  let inviteEmail = $state('');
  let inviteRole = $state('pm_staff');
  let inviteProperties = $state([]);
  let submitting = $state(false);

  let grantTarget = $state(null);
  let grantSelection = $state([]);
  let savingGrants = $state(false);

  let removeTarget = $state(null);
  let busy = $state(null);

  const roleLabels = { pm_staff: 'PM staff', property_owner: 'Property owner' };

  async function load() {
    loading = true;
    error = null;
    try {
      const [result, props] = await Promise.all([
        api.listTenantMembers(tenant.id),
        api.listProperties({ tenant_id: tenant.id, limit: 100 }),
      ]);
      members = result.members;
      invitations = result.invitations;
      properties = props.items;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenant.id; load(); });

  async function submitInvite(event) {
    event.preventDefault();
    submitting = true;
    try {
      const result = await api.createInvitation({
        email: inviteEmail,
        tenant_id: tenant.id,
        role: inviteRole,
        property_ids: inviteRole === 'property_owner' ? inviteProperties.map(Number) : [],
      });
      if (result?.claimed) {
        toast.success(`${inviteEmail} already had an account — access granted now.`);
      } else if (result?.email_sent) {
        toast.success(`Invitation emailed to ${inviteEmail}`);
      } else {
        toast.info(
          `Invitation created for ${inviteEmail}, but no email was sent. `
          + 'Send them the portal link yourself.'
        );
      }
      showInvite = false;
      inviteEmail = '';
      inviteProperties = [];
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      submitting = false;
    }
  }

  async function run(key, fn, success) {
    busy = key;
    try {
      await fn();
      if (success) toast.success(success);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      busy = null;
    }
  }

  function changeRole(member, role) {
    if (role === member.role) return;
    run(`role-${member.user_id}`,
        () => api.changeMemberRole(tenant.id, member.user_id, role),
        `${member.email} is now ${roleLabels[role]}`);
  }

  function openGrants(member) {
    grantTarget = member;
    grantSelection = [...member.property_ids];
  }

  function toggleGrant(id) {
    grantSelection = grantSelection.includes(id)
      ? grantSelection.filter((p) => p !== id)
      : [...grantSelection, id];
  }

  async function saveGrants() {
    savingGrants = true;
    try {
      await api.setMemberProperties(tenant.id, grantTarget.user_id, grantSelection.map(Number));
      toast.success(`Updated property access for ${grantTarget.email}`);
      grantTarget = null;
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      savingGrants = false;
    }
  }

  function confirmRemove() {
    const target = removeTarget;
    removeTarget = null;
    run(`active-${target.user_id}`,
        () => api.deactivateMember(tenant.id, target.user_id),
        `Access removed for ${target.email}`);
  }
</script>

<section class="space-y-6">
  <div class="flex items-center justify-between">
    <div>
      <h2 class="text-sm font-semibold">Portal users</h2>
      <p class="text-xs p-faint">People who can sign in to the portal for {tenant.name}.</p>
    </div>
    <button type="button" class="p-btn p-btn-primary" disabled={!tenant.is_active}
            title={tenant.is_active ? undefined : 'Reactivate the tenant to invite users'}
            onclick={() => (showInvite = !showInvite)}>
      <i class="fas fa-user-plus" aria-hidden="true"></i> Invite
    </button>
  </div>

  {#if showInvite}
    <form class="p-card space-y-4 p-4" onsubmit={submitInvite}>
      <div class="grid gap-4 sm:grid-cols-2">
        <div>
          <label for="m-email" class="text-xs font-medium p-muted">Email</label>
          <input id="m-email" class="p-input mt-1" type="email" bind:value={inviteEmail} required />
        </div>
        <div>
          <label for="m-role" class="text-xs font-medium p-muted">Role</label>
          <select id="m-role" class="p-input mt-1" bind:value={inviteRole}>
            <option value="pm_staff">PM staff (whole tenant)</option>
            <option value="property_owner">Property owner (selected properties)</option>
          </select>
        </div>
      </div>
      {#if inviteRole === 'property_owner'}
        <div>
          <label for="m-props" class="text-xs font-medium p-muted">
            Properties ({inviteProperties.length} selected)
          </label>
          <select id="m-props" class="p-input mt-1 h-40" multiple bind:value={inviteProperties}>
            {#each properties as property}
              <option value={property.id}>{property.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <p class="text-xs p-faint">
        If this address already has an account, access is granted immediately. Otherwise they
        are emailed a link to register with this exact address.
      </p>
      <div class="flex justify-end gap-2">
        <button type="button" class="p-btn p-btn-quiet" onclick={() => (showInvite = false)}>Cancel</button>
        <button type="submit" class="p-btn p-btn-primary" disabled={submitting}>Send invitation</button>
      </div>
    </form>
  {/if}

  {#if error}
    <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
  {:else if loading}
    <p class="p-card p-4 text-sm p-muted">Loading…</p>
  {:else}
    <div class="p-card overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead>
          <tr style="border-bottom: 1px solid var(--p-border)">
            <th class="px-4 py-3 text-xs font-semibold uppercase tracking-wide p-muted">User</th>
            <th class="px-4 py-3 text-xs font-semibold uppercase tracking-wide p-muted">Role</th>
            <th class="px-4 py-3 text-xs font-semibold uppercase tracking-wide p-muted">Properties</th>
            <th class="px-4 py-3 text-xs font-semibold uppercase tracking-wide p-muted">Last sign-in</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {#each members as member (member.user_id)}
            <tr style="border-top: 1px solid var(--p-border)" class:opacity-60={!member.membership_active}>
              <td class="px-4 py-3">
                <p>{member.email}</p>
                <p class="text-xs p-faint">
                  {member.full_name ?? ''}
                  {#if !member.membership_active}· access removed{/if}
                  {#if !member.user_active}· account disabled{/if}
                </p>
              </td>
              <td class="px-4 py-3">
                <select class="p-input text-xs" value={member.role}
                        disabled={!member.membership_active || busy === `role-${member.user_id}`}
                        onchange={(e) => changeRole(member, e.currentTarget.value)}>
                  <option value="pm_staff">PM staff</option>
                  <option value="property_owner">Property owner</option>
                </select>
              </td>
              <td class="px-4 py-3">
                {#if member.role === 'property_owner'}
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                          disabled={!member.membership_active} onclick={() => openGrants(member)}>
                    {member.property_count} selected
                  </button>
                {:else}
                  <span class="text-xs p-muted">All</span>
                {/if}
              </td>
              <td class="px-4 py-3 text-xs p-muted">
                {member.last_login ? new Date(member.last_login).toLocaleString() : 'Never'}
              </td>
              <td class="px-4 py-3 text-right">
                {#if member.membership_active}
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                          style="color: var(--p-danger)"
                          disabled={member.user_id === authStore.context?.user_id}
                          onclick={() => (removeTarget = member)}>Remove</button>
                {:else}
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                          disabled={busy === `active-${member.user_id}`}
                          onclick={() => run(`active-${member.user_id}`,
                                             () => api.reactivateMember(tenant.id, member.user_id),
                                             `Access restored for ${member.email}`)}>
                    Restore
                  </button>
                {/if}
              </td>
            </tr>
          {:else}
            <tr><td colspan="5" class="px-4 py-8 text-center p-muted">No portal users yet.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>

    {#if invitations.length}
      <div>
        <h3 class="mb-2 text-sm font-semibold">Pending invitations</h3>
        <div class="p-card divide-y" style="border-color: var(--p-border)">
          {#each invitations as invite (invite.id)}
            {@const expired = new Date(invite.expires_at) < new Date()}
            <div class="flex items-center justify-between gap-4 px-4 py-3 text-sm">
              <div>
                <p>{invite.invitee_email}</p>
                <p class="text-xs p-faint">
                  {roleLabels[invite.role] ?? invite.role}
                  · {expired ? 'expired' : 'expires'} {new Date(invite.expires_at).toLocaleDateString()}
                </p>
              </div>
              <div class="flex gap-1">
                <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                        disabled={busy === `resend-${invite.id}`}
                        onclick={() => run(`resend-${invite.id}`, async () => {
                          const r = await api.resendInvitation(invite.id);
                          if (!r?.email_sent) toast.info('Expiry extended, but no email was sent (SMTP not configured).');
                        }, 'Invitation re-sent and extended 14 days')}>Resend</button>
                <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                        onclick={() => run(`revoke-${invite.id}`,
                                           () => api.revokeInvitation(invite.id),
                                           'Invitation revoked')}>Revoke</button>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  {/if}
</section>

{#if grantTarget}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
       role="presentation" onclick={(e) => { if (e.target === e.currentTarget) grantTarget = null; }}>
    <div class="p-card w-full max-w-lg p-5" role="dialog" aria-modal="true" aria-label="Property access">
      <h2 class="text-sm font-semibold">Property access</h2>
      <p class="mt-1 text-xs p-faint">{grantTarget.email}</p>
      {#if !properties.length}
        <p class="mt-4 text-sm p-muted">This tenant has no properties yet.</p>
      {:else}
        <div class="mt-3 max-h-64 space-y-1 overflow-y-auto rounded-lg border p-2"
             style="border-color: var(--p-border)">
          {#each properties as property (property.id)}
            <label class="flex items-center gap-2 rounded px-2 py-1 text-sm">
              <input type="checkbox" checked={grantSelection.includes(property.id)}
                     onchange={() => toggleGrant(property.id)} />
              <span>{property.name}</span>
            </label>
          {/each}
        </div>
        <p class="mt-1 text-xs p-faint">
          Saving replaces this owner's access at {tenant.name} with exactly what is checked.
        </p>
      {/if}
      <div class="mt-5 flex justify-end gap-2">
        <button type="button" class="p-btn p-btn-quiet" onclick={() => (grantTarget = null)}>Cancel</button>
        <button type="button" class="p-btn p-btn-primary" disabled={savingGrants} onclick={saveGrants}>
          Save access
        </button>
      </div>
    </div>
  </div>
{/if}

<ConfirmModal open={!!removeTarget}
              title="Remove access to this tenant?"
              message={removeTarget
                ? `${removeTarget.email} will lose portal access to ${tenant.name}. You can restore it later.`
                : ''}
              confirmLabel="Remove access"
              onConfirm={confirmRemove}
              onCancel={() => (removeTarget = null)} />
